import Phaser from "phaser";
import { JOBS, isValidNickname, ZoneId, DEFAULT_ZONE } from "@ordinarydeveloper/shared";
import { API_URL } from "../serverUrl";

export class CharacterScene extends Phaser.Scene {
  private overlay!: HTMLDivElement;
  private selectedJob: string | null = null;

  constructor() {
    super({ key: "CharacterScene" });
  }

  create() {
    this.buildOverlay();
  }

  private removeOverlay() {
    if (this.overlay?.parentNode) this.overlay.parentNode.removeChild(this.overlay);
  }

  private buildOverlay() {
    this.overlay = document.createElement("div");
    this.overlay.style.cssText = [
      "position:fixed;inset:0;display:flex;align-items:center;justify-content:center",
      "background:rgba(10,10,30,0.97);z-index:200;font-family:monospace",
    ].join(";");

    const card = document.createElement("div");
    card.style.cssText = [
      "width:440px;max-width:92vw;background:#1e1e3a;border-radius:12px;padding:32px 28px",
      "border:1px solid rgba(255,255,255,0.1);box-shadow:0 8px 32px rgba(0,0,0,0.6)",
    ].join(";");

    const title = document.createElement("h1");
    title.textContent = "캐릭터 생성";
    title.style.cssText = "color:#fff;font-size:20px;margin:0 0 6px;text-align:center;font-weight:700";

    const sub = document.createElement("div");
    sub.textContent = "너의 개발자 여정이 시작된다.";
    sub.style.cssText = "color:rgba(255,255,255,0.5);font-size:12px;text-align:center;margin-bottom:22px";

    // Nickname
    const nickWrap = document.createElement("div");
    nickWrap.style.cssText = "margin-bottom:18px";
    const nickInput = document.createElement("input");
    nickInput.placeholder = "닉네임 (2~12자, 한글/영문/숫자)";
    nickInput.maxLength = 12;
    nickInput.style.cssText = [
      "width:100%;box-sizing:border-box;padding:11px 14px",
      "background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.15)",
      "color:#fff;font-size:14px;border-radius:8px;outline:none;font-family:monospace",
    ].join(";");
    nickInput.addEventListener("keydown", (e) => e.stopPropagation());
    nickWrap.appendChild(nickInput);

    // Job cards
    const jobLabel = document.createElement("div");
    jobLabel.textContent = "직업 선택";
    jobLabel.style.cssText = "color:rgba(255,255,255,0.7);font-size:13px;margin-bottom:10px";

    const jobRow = document.createElement("div");
    jobRow.style.cssText = "display:flex;gap:12px;margin-bottom:20px";

    const jobCards: Record<string, HTMLDivElement> = {};
    const paintCards = () => {
      for (const [id, el] of Object.entries(jobCards)) {
        const active = id === this.selectedJob;
        el.style.borderColor = active ? "#3d8b4e" : "rgba(255,255,255,0.15)";
        el.style.background  = active ? "rgba(61,139,78,0.18)" : "rgba(255,255,255,0.04)";
      }
    };

    for (const job of Object.values(JOBS)) {
      const cardEl = document.createElement("div");
      cardEl.style.cssText = [
        "flex:1;padding:14px 12px;border-radius:10px;cursor:pointer",
        "border:2px solid rgba(255,255,255,0.15);background:rgba(255,255,255,0.04)",
        "transition:all 0.15s;text-align:center",
      ].join(";");

      const icon = document.createElement("div");
      icon.textContent = job.icon;
      icon.style.cssText = "font-size:34px;margin-bottom:6px";

      const name = document.createElement("div");
      name.textContent = job.name;
      name.style.cssText = "color:#fff;font-size:14px;font-weight:700;margin-bottom:8px";

      const desc = document.createElement("div");
      desc.textContent = job.desc;
      desc.style.cssText = "color:rgba(255,255,255,0.55);font-size:11px;line-height:1.5";

      cardEl.append(icon, name, desc);
      cardEl.addEventListener("click", () => {
        this.selectedJob = job.id;
        paintCards();
        errorMsg.textContent = "";
      });
      jobCards[job.id] = cardEl;
      jobRow.appendChild(cardEl);
    }

    const submitBtn = document.createElement("button");
    submitBtn.textContent = "모험 시작";
    submitBtn.style.cssText = [
      "width:100%;padding:12px;background:#3d8b4e;color:#fff;border:none",
      "border-radius:8px;font-size:15px;cursor:pointer;font-family:monospace;font-weight:600",
    ].join(";");

    const errorMsg = document.createElement("div");
    errorMsg.style.cssText =
      "color:#ff6b6b;font-size:13px;margin-top:12px;min-height:18px;text-align:center";

    const handleSubmit = async () => {
      const nickname = nickInput.value.trim();
      if (!isValidNickname(nickname)) {
        errorMsg.textContent = "닉네임은 2~12자의 한글/영문/숫자여야 합니다";
        return;
      }
      if (!this.selectedJob) {
        errorMsg.textContent = "직업을 선택해주세요";
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = "생성 중...";
      errorMsg.textContent = "";

      try {
        const res = await fetch(`${API_URL}/auth/character`, {
          method:      "POST",
          headers:     { "Content-Type": "application/json" },
          credentials: "include",
          body:        JSON.stringify({ nickname, job: this.selectedJob }),
        });
        const json = await res.json() as {
          error?: string;
          user?: { nickname: string; job: string; currentZone: ZoneId };
          joinToken?: string;
        };
        if (!res.ok) {
          errorMsg.textContent = json.error ?? "오류가 발생했습니다";
          return;
        }
        this.removeOverlay();
        this.scene.start("GameScene", {
          nickname:  json.user!.nickname,
          job:       json.user!.job,
          joinToken: json.joinToken!,
          zone:      json.user!.currentZone ?? DEFAULT_ZONE,
        });
      } catch {
        errorMsg.textContent = "서버에 연결할 수 없습니다";
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = "모험 시작";
      }
    };

    submitBtn.addEventListener("click", () => { void handleSubmit(); });
    nickInput.addEventListener("keydown", (e) => {
      // 한글 닉네임 IME 조합 확정 Enter(keyCode 229)로 중복 제출되는 것 방지
      if (e.isComposing || e.keyCode === 229) return;
      if (e.key === "Enter") void handleSubmit();
    });

    paintCards();
    card.append(title, sub, nickWrap, jobLabel, jobRow, submitBtn, errorMsg);
    this.overlay.appendChild(card);
    document.body.appendChild(this.overlay);
  }
}
